/**
 * QueryBuilder host-state context.
 *
 * The pure RuleConditionsQueryBuilder threads cross-cutting state (form-submit
 * state, future flags…) down to RQB's controlElement adapters via this single
 * context, rather than coupling the adapters to Formik or any specific host
 * state library.
 */

import { createContext } from 'react';

import type { FieldDefinitionDto } from './types';

export interface QueryBuilderContextValue {
  /** True when "Required" / error markers should be surfaced (e.g. after the
   *  surrounding form has been submitted at least once). The Formik wrapper
   *  feeds it `submitCount > 0`. */
  shouldShowErrors: boolean;
  /** The registry's `date` / `datetime` entries, for the target picker on a date
   *  leaf, and for the precision a picked target implies. RQB hands a ValueEditor
   *  only its *own* `fieldData`, so the sibling fields have to arrive out of band.
   *  Involved-party (variant) fields are excluded — their catalog path is synthetic,
   *  so the compiler rejects one as an anchor. */
  dateFields: FieldDefinitionDto[];
  /** The registry's `user` entries, for the "compared to" picker on a user leaf — a user is
   *  compared only with another user. Variant fields are excluded for the reason date ones are. */
  userFields: FieldDefinitionDto[];
  /** Whether the rule being authored has a triggering event, and so whether the
   *  target picker offers "When the event occurred". A rule with none has no event
   *  time to evaluate that target against, and the BE refuses it at save. Threaded
   *  through context rather than a prop so a collection's nested builder, which
   *  RQB renders with none of this builder's props, offers the same targets. */
  hasTriggeringEvent: boolean;
  /** Whether a leaf's stored field path is one the registry does not serve.
   *
   *  The catalog is derived live from organization configuration while a stored condition is
   *  frozen at authoring time, so a perfectly valid leaf can stop resolving — and the three
   *  controls that render a leaf each answered that differently: the value editor and the
   *  operator selector both fell back to String, and the field picker printed the raw path.
   *  Computed once, from the registry the QB is rendering against, and threaded here so the
   *  three cells of one leaf cannot disagree about whether it is renderable.
   *
   *  RQB's placeholder path (`autoSelectField={false}` seeds a new row with it) is not
   *  "unknown": it is "nothing picked yet", which the field picker itself handles. */
  isUnknownFieldPath: (path: string | undefined) => boolean;
}

export const QueryBuilderContext = createContext<QueryBuilderContextValue>({
  shouldShowErrors: false,
  dateFields: [],
  userFields: [],
  hasTriggeringEvent: false,
  isUnknownFieldPath: () => false,
});
